import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import type {
  AmpBoardTaskGroupsQuery,
  AmpTagFragment,
  AmpTaskRowFragment,
  AmpWorkflowStatusFragment,
} from "src/graphql";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import {
  ARCHIVED,
  endOfComingWeek,
  isTaskCompleted,
  oneOf,
  STATUS_CATEGORIES,
  STATUS_CATEGORY_LABELS,
  type StatusCategory,
  sortStatuses,
  statusCategory,
  TASK_STATE_LABELS,
  TASK_STATES,
  type TaskState,
  taskCategory,
  taskState,
} from "src/pages/projects/utils";

const TASK_LAYOUTS = ["list", "board", "calendar"] as const;
export type TaskLayout = (typeof TASK_LAYOUTS)[number];

export const GROUP_KINDS = [
  "status",
  "assignee",
  "owner",
  "project",
  "dueDate",
  "tag",
  "creator",
  "none",
] as const;
export type GroupKind = (typeof GROUP_KINDS)[number];

export const GROUP_KIND_LABELS: Record<GroupKind, string> = {
  status: "Status",
  assignee: "Assignee",
  owner: "Owner",
  project: TERMS.project.Singular,
  dueDate: "Due date",
  tag: "Tag",
  creator: "Creator",
  none: "No grouping",
};

export const ORDERINGS = [
  "manual",
  "status",
  "dueDate",
  "created",
  "updated",
  "title",
] as const;
export type Ordering = (typeof ORDERINGS)[number];

export const ORDERING_LABELS: Record<Ordering, string> = {
  manual: "Manual",
  status: "Status",
  dueDate: "Due date",
  created: "Created",
  updated: "Updated",
  title: "Title",
};

export const COMPLETED_VISIBILITY = ["all", "week", "none"] as const;
export type CompletedVisibility = (typeof COMPLETED_VISIBILITY)[number];

export const COMPLETED_VISIBILITY_LABELS: Record<CompletedVisibility, string> =
  {
    all: "All",
    week: "Past week",
    none: "None",
  };

export const DISPLAY_PROPERTIES = [
  "status",
  "owners",
  "assignees",
  "project",
  "dueDate",
  "tags",
  "subtasks",
  "reviewers",
  "creator",
  "created",
  "updated",
] as const;
export type DisplayProperty = (typeof DISPLAY_PROPERTIES)[number];

export const DISPLAY_PROPERTY_LABELS: Record<DisplayProperty, string> = {
  status: "Status",
  assignees: "Assignees",
  owners: "Owner",
  project: TERMS.project.Singular,
  dueDate: "Due date",
  tags: "Tags",
  subtasks: TERMS.subtask.Plural,
  reviewers: "Reviewers",
  creator: "Creator",
  created: "Created",
  updated: "Updated",
};

export const DUE_BUCKETS = [
  "overdue",
  "today",
  "week",
  "later",
  "none",
] as const;
export type DueBucket = (typeof DUE_BUCKETS)[number];

export const DUE_BUCKET_LABELS: Record<DueBucket, string> = {
  overdue: "Overdue",
  today: "Due today",
  week: "Due this week",
  later: "Due later",
  none: "No due date",
};

export type TaskViewOptions = {
  layout: TaskLayout;
  grouping: GroupKind;
  subGrouping: GroupKind;
  ordering: Ordering;
  orderDescending: boolean;
  completed: CompletedVisibility;
  showEmptyGroups: boolean;
  properties: DisplayProperty[];
};

/** Every filter is a set of values to keep; an empty set filters nothing. */
export type TaskFilters = {
  /** Status categories, or archived. */
  statusCategories: TaskState[];
  assigneeIds: string[];
  creatorIds: string[];
  projectIds: string[];
};

/** Stands in for "no assignee" among the assignee filter's user ids. */
export const NO_ASSIGNEE = "unassigned";

/** Keys the group of tasks with no one of a kind on them, and names it as a drag's origin. */
export const NOBODY = "nobody";

/** Stands in for the viewer among the people filters' user ids, so a saved "mine" view follows whoever opens it. */
export const ME = "me";

/** Swaps the "me" filter value for the viewer; without one, "me" matches no one. */
export function resolveMe(
  ids: string[],
  currentUserId: string | undefined,
): string[] {
  return ids.flatMap((id) =>
    id === ME ? (currentUserId === undefined ? [] : [currentUserId]) : [id],
  );
}

export const EMPTY_FILTERS: TaskFilters = {
  statusCategories: [],
  assigneeIds: [],
  creatorIds: [],
  projectIds: [],
};

export const DEFAULT_VIEW_OPTIONS: TaskViewOptions = {
  layout: "list",
  grouping: "status",
  subGrouping: "none",
  ordering: "manual",
  orderDescending: false,
  completed: "all",
  showEmptyGroups: true,
  properties: ["status", "owners", "project", "dueDate", "tags", "subtasks"],
};

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function stringsOf(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

/**
 * Stored views outlive the options they were saved with, so each field is
 * checked and a bad one falls back to the default.
 */
export function readViewOptions(
  stored: unknown,
  defaults: TaskViewOptions,
): TaskViewOptions {
  if (typeof stored !== "object" || stored === null) return defaults;
  const value = stored as Record<string, unknown>;
  const pick = <K extends keyof TaskViewOptions>(
    key: K,
    isValid: (candidate: unknown) => candidate is TaskViewOptions[K],
  ): TaskViewOptions[K] => {
    const candidate = value[key];
    return isValid(candidate) ? candidate : defaults[key];
  };
  return {
    layout: pick("layout", oneOf(TASK_LAYOUTS)),
    grouping: pick("grouping", oneOf(GROUP_KINDS)),
    subGrouping: pick("subGrouping", oneOf(GROUP_KINDS)),
    ordering: pick("ordering", oneOf(ORDERINGS)),
    orderDescending: pick("orderDescending", isBoolean),
    completed: pick("completed", oneOf(COMPLETED_VISIBILITY)),
    showEmptyGroups: pick("showEmptyGroups", isBoolean),
    properties: Array.isArray(value.properties)
      ? value.properties.filter(oneOf(DISPLAY_PROPERTIES))
      : defaults.properties,
  };
}

export function readFilters(stored: unknown): TaskFilters {
  if (typeof stored !== "object" || stored === null) return EMPTY_FILTERS;
  const value = stored as Record<string, unknown>;
  return {
    statusCategories: stringsOf(value.statusCategories).filter(
      oneOf(TASK_STATES),
    ),
    assigneeIds: stringsOf(value.assigneeIds),
    // A saved owner filter is dropped rather than read as creators, which would hide tasks the person owns but didn't create.
    creatorIds: stringsOf(value.creatorIds),
    projectIds: stringsOf(value.projectIds),
  };
}

/** A group's member user ids, by group id. */
export type GroupMemberIds = ReadonlyMap<string, string[]>;

/**
 * Whether any of a task's people is among the ids, where a group stands for its
 * members and the none id for having no one, user or group, as the no-one group does.
 */
function matchesPeople(
  people: Pick<
    AmpTaskRowFragment["assignees"][number],
    "user_id" | "user_group_id"
  >[],
  ids: string[],
  noneId: string,
  groupMemberIds: GroupMemberIds,
): boolean {
  const keys =
    people.length === 0
      ? [noneId]
      : people.flatMap((person) =>
          person.user_id === null
            ? (groupMemberIds.get(String(person.user_group_id)) ?? [])
            : [String(person.user_id)],
        );
  return keys.some((key) => ids.includes(key));
}

export function activeFilterCount(filters: TaskFilters): number {
  return Object.values(filters).reduce((sum, values) => sum + values.length, 0);
}

export function dueBucket(
  dueAt: string | null,
  { now }: { now: Date },
): DueBucket {
  if (!dueAt) return "none";
  const due = new Date(dueAt);
  if (due < now) return "overdue";
  if (due < addDays(startOfDay(now), 1)) return "today";
  if (due < endOfComingWeek(now)) return "week";
  return "later";
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Keeps the tasks that match the filters, the search and the completed option. */
export function filterTasks<T extends AmpTaskRowFragment>(
  tasks: T[],
  {
    filters,
    search,
    completed,
    currentUserId,
    groupMemberIds = new Map(),
    now = new Date(),
  }: {
    filters: TaskFilters;
    search: string;
    completed: CompletedVisibility;
    /** Resolves the "me" filter value; without it, "me" matches no one. */
    currentUserId?: string;
    /** Lets a person filter match tasks given to the person's groups, as the server does. */
    groupMemberIds?: GroupMemberIds;
    now?: Date;
  },
): T[] {
  const query = search.trim().toLowerCase();
  const assigneeIds = resolveMe(filters.assigneeIds, currentUserId);
  const creatorIds = resolveMe(filters.creatorIds, currentUserId);
  // Asking for done or archived tasks by status shows them whatever the completed option says.
  const isCompletedAsked = filters.statusCategories.some(
    (state) => state === "done" || state === ARCHIVED,
  );
  return tasks.filter((task) => {
    if (!isCompletedAsked && isTaskCompleted(task)) {
      if (completed === "none") return false;
      // Completion time isn't stored, so the last update stands in for it.
      if (
        completed === "week" &&
        now.getTime() - new Date(task.updated_at).getTime() > WEEK_MS
      ) {
        return false;
      }
    }
    if (
      filters.statusCategories.length > 0 &&
      !filters.statusCategories.includes(taskState(task))
    ) {
      return false;
    }
    if (
      filters.assigneeIds.length > 0 &&
      !matchesPeople(task.assignees, assigneeIds, NO_ASSIGNEE, groupMemberIds)
    ) {
      return false;
    }
    if (
      filters.creatorIds.length > 0 &&
      !creatorIds.includes(String(task.created_by ?? ""))
    ) {
      return false;
    }
    if (
      filters.projectIds.length > 0 &&
      !filters.projectIds.includes(task.project_id)
    ) {
      return false;
    }
    if (query && !task.title.toLowerCase().includes(query)) return false;
    return true;
  });
}

function compareStrings(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

// Archived tasks rank past every status, and among themselves by when they were archived, as the server orders them.
function statusRank(task: AmpTaskRowFragment): number {
  if (task.deleted_at) return TASK_STATES.length * 1000;
  const category = TASK_STATES.indexOf(taskState(task));
  return category * 1000 + (task.status?.position ?? 0);
}

function time(iso: string | null): number {
  return iso ? new Date(iso).getTime() : 0;
}

const DATE_ORDERING_FIELDS = {
  dueDate: "due_at",
  created: "created_at",
  updated: "updated_at",
} as const satisfies Partial<Record<Ordering, keyof AmpTaskRowFragment>>;

export function sortTasks<T extends AmpTaskRowFragment>(
  tasks: T[],
  ordering: Ordering,
  descending: boolean,
): T[] {
  const direction = descending ? -1 : 1;
  const compare = (a: T, b: T): number => {
    switch (ordering) {
      // A manual order is the one people arranged, so it has no direction.
      case "manual":
        return a.position - b.position;
      case "status":
        return (
          (statusRank(a) - statusRank(b) ||
            time(a.deleted_at) - time(b.deleted_at)) * direction
        );
      case "title":
        return compareStrings(a.title, b.title) * direction;
      default: {
        const field = DATE_ORDERING_FIELDS[ordering];
        const [first, second] = [a[field], b[field]];
        // Tasks without a due date sort last in both directions.
        if (!first || !second) return (first ? 0 : 1) - (second ? 0 : 1);
        return (
          (new Date(first).getTime() - new Date(second).getTime()) * direction
        );
      }
    }
  };
  // The id settles the rest, as the server's order does, so pages join without reshuffling.
  return [...tasks].sort(
    (a, b) =>
      compare(a, b) ||
      compareStrings(a.title, b.title) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** What a group header shows beside its label. */
export type GroupIcon =
  | { kind: "status"; status: AmpWorkflowStatusFragment }
  | { kind: "category"; category: TaskState }
  | { kind: "user"; name: string }
  | { kind: "color"; color: string | null }
  | { kind: "project"; icon: string | null }
  | { kind: "none" };

export type TaskGroup<T> = {
  key: string;
  label: string;
  icon: GroupIcon;
  /** Set on category groups, where a drop takes the category's first status. */
  category: StatusCategory | null;
  /** The archived tasks' group, where a drop archives the task. */
  isArchive: boolean;
  /** Set on assignee and owner groups, where a drop hands the task over; null there means no one. */
  personId: string | null | undefined;
  /** Set on project groups, which link to the project and create tasks in it. */
  projectId?: string;
  /** Set on a user group's task group, whose tasks load by it. */
  userGroupId?: string;
  /** How many tasks the server holds for the group, of which `tasks` are the ones loaded. */
  count?: number;
  tasks: T[];
};

type GroupContext = {
  /**
   * The statuses of the one workflow in view. Without it, status groups are
   * categories, since tasks from many workflows share no statuses.
   */
  statuses: AmpWorkflowStatusFragment[] | null;
  showEmptyGroups: boolean;
  /** Every project in view, so an empty one still gets a project group. */
  projects?: { id: string; title: string; icon: string | null }[];
  counts?: AmpBoardTaskGroupsQuery;
  now?: Date;
};

/** Keys the group of tasks with no tag. */
export const UNTAGGED = "untagged";

const ALL = "all";

const DUE_COUNT_FIELDS = {
  overdue: "overdue",
  today: "due_today",
  week: "due_week",
  later: "due_later",
  none: "undated",
} as const satisfies Record<DueBucket, keyof AmpBoardTaskGroupsQuery>;

function emptyGroup<T>(
  key: string,
  label: string,
  icon: GroupIcon,
): TaskGroup<T> {
  return {
    key,
    label,
    icon,
    category: null,
    isArchive: false,
    personId: undefined,
    tasks: [],
  };
}

type TaskPerson = AmpTaskRowFragment["assignees"][number];

/**
 * A person's group: a user's, which a drop can hand the task to, or a user
 * group's, which it can't, since a drop writes only users.
 */
function personGroup<T>(person: TaskPerson): TaskGroup<T> {
  if (person.user_id === null) {
    const userGroupId = String(person.user_group_id);
    const name = person.user_group?.name ?? "Unknown group";
    return {
      ...emptyGroup<T>(`group:${userGroupId}`, name, { kind: "user", name }),
      userGroupId,
    };
  }
  const key = String(person.user_id);
  const name = person.user?.name ?? "Unknown";
  return { ...emptyGroup(key, name, { kind: "user", name }), personId: key };
}

function nobodyGroup<T>(kind: GroupKind): TaskGroup<T> {
  const label = kind === "assignee" ? "Unassigned" : "No owner";
  return {
    ...emptyGroup<T>(NOBODY, label, { kind: "none" }),
    personId: null,
  };
}

function creatorGroup<T>(
  createdBy: unknown,
  user: { name: string } | null,
): TaskGroup<T> {
  if (createdBy == null) return emptyGroup(NOBODY, "Unknown", { kind: "none" });
  const name = user?.name ?? "Unknown";
  return emptyGroup(String(createdBy), name, { kind: "user", name });
}

function projectGroup<T>(project: {
  id: string;
  title: string;
  icon: string | null;
}): TaskGroup<T> {
  return {
    ...emptyGroup<T>(project.id, project.title, {
      kind: "project",
      icon: project.icon,
    }),
    projectId: project.id,
  };
}

function tagGroup<T>(tag: AmpTagFragment): TaskGroup<T> {
  return emptyGroup(tag.id, tag.name, { kind: "color", color: tag.color });
}

function untaggedGroup<T>(): TaskGroup<T> {
  return emptyGroup(UNTAGGED, "No tag", { kind: "none" });
}

type Counted = { aggregate?: { count: number } | null } | null | undefined;

function countOf(value: Counted): number {
  return value?.aggregate?.count ?? 0;
}

/**
 * What the server counted: each group's count by key, and the groups only it
 * knows of yet, such as a person none of whose tasks has loaded.
 */
function serverGroups<T>(
  kind: GroupKind,
  counts: AmpBoardTaskGroupsQuery,
  statuses: AmpWorkflowStatusFragment[] | null,
) {
  const byKey = new Map<string, number>();
  const seeds: TaskGroup<T>[] = [];
  const seed = (group: TaskGroup<T>, value: Counted) => {
    byKey.set(group.key, countOf(value));
    if (countOf(value) > 0) seeds.push(group);
  };
  switch (kind) {
    case "none":
      byKey.set(ALL, countOf(counts.total));
      break;
    case "status":
      for (const status of counts.amp_workflow_statuses ?? []) {
        const key = statuses ? status.id : statusCategory(status);
        const count = countOf(status.tasks_aggregate);
        byKey.set(key, (byKey.get(key) ?? 0) + count);
      }
      byKey.set(ARCHIVED, countOf(counts.archived));
      break;
    case "dueDate":
      for (const bucket of DUE_BUCKETS) {
        byKey.set(bucket, countOf(counts[DUE_COUNT_FIELDS[bucket]]));
      }
      break;
    case "assignee":
    case "owner":
      for (const person of counts.people ?? []) {
        seed(personGroup(person), person.peers_aggregate);
      }
      for (const row of counts.groups ?? []) {
        seed(personGroup(row), row.group_peers_aggregate);
      }
      seed(nobodyGroup(kind), counts.nobody);
      break;
    case "creator":
      for (const row of counts.creators ?? []) {
        seed(
          creatorGroup(row.created_by, row.created_by_user),
          row.creator_tasks_aggregate,
        );
      }
      seed(creatorGroup(null, null), counts.no_creator);
      break;
    case "project":
      for (const project of counts.amp_projects ?? []) {
        seed(projectGroup(project), project.tasks_aggregate);
      }
      break;
    case "tag":
      for (const tag of counts.amp_tags ?? []) {
        seed(tagGroup(tag), tag.task_tags_aggregate);
      }
      seed(untaggedGroup(), counts.untagged);
      break;
  }
  return { byKey, seeds };
}

/** Fills fixed groups, in their fixed order, with their tasks. */
function fillGroups<T>(
  groups: TaskGroup<T>[],
  tasks: T[],
  keyOf: (task: T) => string | null,
): TaskGroup<T>[] {
  const byKey = new Map(groups.map((group) => [group.key, group]));
  for (const task of tasks) {
    const key = keyOf(task);
    if (key !== null) byKey.get(key)?.tasks.push(task);
  }
  return groups;
}

/** Orders groups by label, with the group of tasks with no one or nothing last. */
function byLabelNoneLast(noneKey: string) {
  return <T>(a: TaskGroup<T>, b: TaskGroup<T>) => {
    if (a.key === noneKey) return 1;
    if (b.key === noneKey) return -1;
    return compareStrings(a.label, b.label);
  };
}

/**
 * Splits tasks into groups, keeping each group's tasks in the order given.
 * A task with several tags, assignees or owners appears under each of them.
 * With counts, a group the server holds tasks for shows before any of them load.
 */
export function groupTasks<T extends AmpTaskRowFragment>(
  tasks: T[],
  kind: GroupKind,
  {
    statuses,
    showEmptyGroups,
    projects,
    counts,
    now = new Date(),
  }: GroupContext,
): TaskGroup<T>[] {
  const server = counts ? serverGroups<T>(kind, counts, statuses) : undefined;
  const groups = new Map<string, TaskGroup<T>>();
  const add = (group: TaskGroup<T>, task: T | null) => {
    const existing = groups.get(group.key) ?? group;
    if (task) existing.tasks.push(task);
    groups.set(group.key, existing);
  };
  for (const seed of server?.seeds ?? []) add(seed, null);
  // A group shows with tasks loaded or on the server; an empty one only when asked for, and the archive never.
  const finish = (list: TaskGroup<T>[]) =>
    list
      .map((group) => ({ ...group, count: server?.byKey.get(group.key) }))
      .filter(
        (group) =>
          group.tasks.length > 0 ||
          (group.count ?? 0) > 0 ||
          (showEmptyGroups && !group.isArchive),
      );

  switch (kind) {
    case "none": {
      const group = emptyGroup<T>(ALL, `All ${TERMS.task.plural}`, {
        kind: "none",
      });
      group.tasks.push(...tasks);
      return [{ ...group, count: server?.byKey.get(ALL) }];
    }
    case "status": {
      // Archived tasks leave their status's group for one of their own, last.
      const archive: TaskGroup<T> = {
        ...emptyGroup<T>(ARCHIVED, TASK_STATE_LABELS.archived, {
          kind: "category",
          category: ARCHIVED,
        }),
        isArchive: true,
        tasks: tasks.filter((task) => task.deleted_at !== null),
      };
      const inPlay = tasks.filter((task) => task.deleted_at === null);
      if (statuses) {
        // A task's own status stands in for one the workflow doesn't list yet, as while it loads.
        const byId = new Map(statuses.map((status) => [status.id, status]));
        for (const task of inPlay) {
          if (task.status && !byId.has(task.status.id))
            byId.set(task.status.id, task.status);
        }
        const make = (status: AmpWorkflowStatusFragment): TaskGroup<T> => ({
          ...emptyGroup<T>(status.id, status.name, { kind: "status", status }),
          category: statusCategory(status),
        });
        return finish([
          ...fillGroups(
            sortStatuses([...byId.values()]).map(make),
            inPlay,
            (task) => task.status_id,
          ),
          archive,
        ]);
      }
      const make = (category: StatusCategory): TaskGroup<T> => ({
        ...emptyGroup<T>(category, STATUS_CATEGORY_LABELS[category], {
          kind: "category",
          category,
        }),
        category,
      });
      return finish([
        ...fillGroups(STATUS_CATEGORIES.map(make), inPlay, taskCategory),
        archive,
      ]);
    }
    case "assignee":
    case "owner": {
      for (const task of tasks) {
        const people = kind === "owner" ? task.owners : task.assignees;
        // Only a task with no user and no group is no one's, as the no-one filters read it.
        if (people.length === 0) add(nobodyGroup(kind), task);
        for (const person of people) add(personGroup(person), task);
      }
      return finish([...groups.values()].sort(byLabelNoneLast(NOBODY)));
    }
    case "creator": {
      for (const task of tasks) {
        add(creatorGroup(task.created_by, task.created_by_user), task);
      }
      return finish([...groups.values()].sort(byLabelNoneLast(NOBODY)));
    }
    case "project": {
      if (showEmptyGroups)
        for (const project of projects ?? []) add(projectGroup(project), null);
      for (const task of tasks) {
        add(
          projectGroup({
            id: task.project_id,
            title: task.project?.title ?? TERMS.project.Singular,
            icon: task.project?.icon ?? null,
          }),
          task,
        );
      }
      return finish(
        [...groups.values()].sort((a, b) => compareStrings(a.label, b.label)),
      );
    }
    case "dueDate":
      return finish(
        fillGroups(
          DUE_BUCKETS.map((bucket) =>
            emptyGroup<T>(bucket, DUE_BUCKET_LABELS[bucket], { kind: "none" }),
          ),
          tasks,
          (task) => dueBucket(task.due_at, { now }),
        ),
      );
    case "tag": {
      for (const task of tasks) {
        const tags = task.tags.flatMap((taskTag) =>
          taskTag.tag ? [taskTag.tag] : [],
        );
        if (tags.length === 0) add(untaggedGroup(), task);
        for (const tag of tags) add(tagGroup(tag), task);
      }
      return finish([...groups.values()].sort(byLabelNoneLast(UNTAGGED)));
    }
  }
}

export type WorkflowSection<T> = {
  key: string;
  label: string;
  /** The projects whose rows sit in this section. */
  projectIds: ReadonlySet<string>;
  /** The workflow's statuses, as status groups. */
  columns: TaskGroup<T>[];
};

/** A section's tasks outside the archive, counted by the server where it has. */
export function sectionCountOf<T>(section: WorkflowSection<T>): number {
  return section.columns.reduce(
    (total, group) =>
      group.isArchive ? total : total + (group.count ?? group.tasks.length),
    0,
  );
}

export type TaskGroupLayout<T> =
  | { kind: "groups"; groups: TaskGroup<T>[] }
  | { kind: "workflow-sections"; sections: WorkflowSection<T>[] };

type ProjectWorkflows = { id: string; workflowIds: readonly string[] };

/** Every workflow each project's tasks take, from the server's counts, which cover tasks not loaded yet. */
export function projectWorkflowsOf({
  counts,
  projects,
}: {
  counts: Pick<AmpBoardTaskGroupsQuery, "project_statuses"> | undefined;
  projects: readonly { id: string; workflow_id: string }[];
}): ProjectWorkflows[] {
  const counted = counts?.project_statuses ?? [];
  return projects.map((project) => ({
    id: project.id,
    workflowIds: [
      ...new Set([
        project.workflow_id,
        ...counted.flatMap((row) =>
          row.project_id === project.id && row.status
            ? [row.status.workflow_id]
            : [],
        ),
      ]),
    ],
  }));
}

/**
 * Splits a board into one section per workflow, so its columns are that
 * workflow's statuses rather than categories. Each task sits under its own
 * workflow; workflows with no task or project in view are left out.
 */
export function groupByWorkflow<T extends AmpTaskRowFragment>(
  tasks: T[],
  {
    workflows,
    projects,
    ...context
  }: Omit<GroupContext, "statuses" | "projects"> & {
    workflows: {
      id: string;
      name: string;
      statuses: AmpWorkflowStatusFragment[];
    }[];
    /** The projects in view, each with every workflow its tasks take. */
    projects: readonly ProjectWorkflows[];
  },
): WorkflowSection<T>[] {
  return workflows.flatMap((workflow) => {
    const workflowTasks = tasks.filter(
      (task) => task.status?.workflow_id === workflow.id,
    );
    const projectIds = new Set([
      ...projects.flatMap((project) =>
        project.workflowIds.includes(workflow.id) ? [project.id] : [],
      ),
      ...workflowTasks.map((task) => task.project_id),
    ]);
    if (projectIds.size === 0) {
      return [];
    }
    const columns = groupTasks(workflowTasks, "status", {
      ...context,
      statuses: workflow.statuses,
    }).flatMap((group) =>
      // The server counts archived tasks across every workflow, so a section's archive shows only what has loaded.
      group.isArchive
        ? group.tasks.length > 0
          ? [{ ...group, count: undefined }]
          : []
        : [group],
    );
    return [{ key: workflow.id, label: workflow.name, projectIds, columns }];
  });
}

/** Groups that a drop can write: status, assignee and owner. */
export function canDropInto(kind: GroupKind): boolean {
  return kind === "status" || kind === "assignee" || kind === "owner";
}

/**
 * A task's assignees or owners after it moves between their groups: the person
 * it left gives way to the one it joined, and the no-one group clears them all.
 */
export function movedPersonIds(
  userIds: string[],
  { from, to }: { from: string | null | undefined; to: string | null },
): string[] {
  if (to === null) return [];
  const kept = userIds.filter((id) => id !== from && id !== to);
  return [...kept, to];
}

export type CalendarDay = {
  /** The local date, as yyyy-MM-dd, which tasks are keyed on too. */
  key: string;
  date: Date;
  isInMonth: boolean;
};

/** The weeks a month view shows: whole weeks, Sunday first, around the month. */
export function calendarWeeks(month: Date): CalendarDay[][] {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month)),
  });
  const weeks: CalendarDay[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(
      days.slice(index, index + 7).map((date) => ({
        key: dayKey(date),
        date,
        isInMonth: isSameMonth(date, month),
      })),
    );
  }
  return weeks;
}

function dayKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** Tasks by the local day they're due, and the ones with no due date. */
export function tasksByDueDay<T extends Pick<AmpTaskRowFragment, "due_at">>(
  tasks: T[],
): { byDay: Map<string, T[]>; unscheduled: T[] } {
  const byDay = new Map<string, T[]>();
  const unscheduled: T[] = [];
  for (const task of tasks) {
    if (!task.due_at) {
      unscheduled.push(task);
      continue;
    }
    const key = dayKey(new Date(task.due_at));
    const day = byDay.get(key);
    if (day) day.push(task);
    else byDay.set(key, [task]);
  }
  return { byDay, unscheduled };
}

/**
 * The selection after clicking a task's box. A shift-click selects every task
 * between it and the last one clicked, in the order shown.
 */
export function toggleSelection({
  selected,
  orderedIds,
  taskId,
  anchorId,
  isRange,
}: {
  selected: ReadonlySet<string>;
  orderedIds: string[];
  taskId: string;
  anchorId: string | null;
  isRange: boolean;
}): Set<string> {
  const next = new Set(selected);
  const from = anchorId === null ? -1 : orderedIds.indexOf(anchorId);
  const to = orderedIds.indexOf(taskId);
  if (isRange && anchorId !== null && from !== -1 && to !== -1) {
    const [start, end] = from < to ? [from, to] : [to, from];
    // The range takes the state the anchor was given.
    const select = selected.has(anchorId);
    for (const id of orderedIds.slice(start, end + 1)) {
      if (select) next.add(id);
      else next.delete(id);
    }
    return next;
  }
  if (next.has(taskId)) next.delete(taskId);
  else next.add(taskId);
  return next;
}
